import clsx from "clsx";
import React, { PropsWithChildren, ReactNode } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-aria-components";

interface TabsCommonProps extends PropsWithChildren {
  className?: string;
}

// compound component example, based on @headlessui/react
function HdTabs(
  props: TabsCommonProps & {
    selectedKey?: string;
    onChange?: (key: string) => void;
  }
) {
  return (
    <Tabs
      selectedKey={String(props.selectedKey)}
      className={props.className}
      onSelectionChange={(key) => {
        props.onChange?.(key as string);
      }}
    >
      {props.children}
    </Tabs>
  );
}

HdTabs.Header = (props: TabsCommonProps) => {
  return (
    <TabList aria-label="Tabs" className={props.className}>
      {React.Children.map(props.children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child) : child
      )}
    </TabList>
  );
};

HdTabs.Tab = (props: {
  id: string;
  disabled?: boolean;
  href?: string;
  className?: string;
  children:
    | ReactNode
    | ((props: {
        hover: boolean;
        selected: boolean;
        disabled: boolean;
        focus: boolean;
      }) => JSX.Element);
}) => {
  return (
    <Tab
      id={props.id}
      isDisabled={props.disabled}
      href={props.href}
      className={clsx(
        props.className,
        "outline-none group inline-block px-4 py-3 data-[selected]:bg-white data-[selected]:border-t-4 data-[selected]:border-t-primary data-[selected]:font-bold",
        !props.disabled && "cursor-pointer"
      )}
    >
      {({ isHovered, isSelected, isDisabled, isFocused }) =>
        typeof props.children === "function"
          ? props.children({
              hover: isHovered,
              selected: isSelected,
              disabled: isDisabled,
              focus: isFocused,
            })
          : props.children
      }
    </Tab>
  );
};

HdTabs.Panel = (
  props: TabsCommonProps & {
    id: string;
  }
) => {
  return (
    <TabPanel id={props.id} className={clsx("p-5 bg-white", props.className)}>
      {props.children}
    </TabPanel>
  );
};

export default HdTabs;
